import React from 'react';
import { ActionDropdown, Button, confirmAlert } from 'components/common';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiList } from 'react-icons/fi';
import { deleteApplication } from 'redux/integration/applicationSlice';
import { escapeHtml } from 'helpers/utils';
import PropTypes from "prop-types";

const APPLICATION_URL = '/integration/applications';

const ApplicationDetailHeader = ({ record }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const applicationId = record?.id;

  const goToList = () => navigate(`${APPLICATION_URL}/list`);
  const goToNew  = () => navigate(`${APPLICATION_URL}/new`);
  const goToEdit = () => navigate(`${APPLICATION_URL}/${applicationId}/edit`, { state: { record } });

  const handleDelete = () => {
    confirmAlert({
      title: 'Delete Application',
      // The dialog renders its message as HTML — an application name is free
      // text, so it is escaped rather than interpolated raw.
      message: `Deleting <strong>${escapeHtml(record?.name || '')}</strong> (ID: ${applicationId}) revokes its client ` +
        'credentials: every integration using them stops working immediately. This cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          // .unwrap() so a refused delete actually throws here: dispatching a
          // thunk resolves with the rejected action, so without it the list
          // opened as if the client were gone while it is still registered.
          await dispatch(deleteApplication(applicationId)).unwrap();
          navigate(`${APPLICATION_URL}/list`);
        } catch (error) {
          // The slice's rejection matcher already reports the reason; staying on
          // this page is the point.
        }
      },
    });
  };

  // The kebab carries what does not deserve a permanent button, plus the two
  // primary actions for narrow screens where those buttons are hidden.
  const menuItems = [
    { key: 'edit', label: 'Edit', icon: <FiEdit2 size={14} />, onClick: goToEdit },
    { key: 'new', label: 'New application', icon: <FiPlus size={14} />, onClick: goToNew },
    { key: 'list', label: 'Back to list', icon: <FiList size={14} />, onClick: goToList },
    { key: 'sep', divider: true },
    { key: 'delete', label: 'Delete', icon: <FiTrash2 size={14} />, danger: true, onClick: handleDelete },
  ];

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200">
      <div className="tw-px-5 tw-py-3 tw-flex tw-items-center tw-justify-between tw-gap-3">

        {/* Left: back + breadcrumb */}
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-min-w-0">
          <button
            onClick={goToList}
            className="tw-p-1.5 tw-rounded tw-text-slate-400 hover:tw-bg-slate-100 hover:tw-text-slate-700 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-outline-none"
            title="Back to list"
            aria-label="Back to list"
          >
            <FiArrowLeft size={16} />
          </button>
          <div className="tw-border-l tw-border-slate-200 tw-pl-3 tw-min-w-0">
            <nav className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-min-w-0">
              <button onClick={goToList} className="tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0 tw-outline-none">
                Applications
              </button>
              <span className="tw-text-slate-300">/</span>
              <span className="tw-text-slate-700 tw-font-medium tw-truncate">{record?.name}</span>
            </nav>
          </div>
        </div>

        {/* Right: actions */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
          <Button size="sm" variant="secondary" onClick={goToNew} className="tw-hidden sm:tw-inline-flex">
            <FiPlus size={13} /> New
          </Button>
          <Button size="sm" variant="secondary" onClick={goToEdit} className="tw-hidden sm:tw-inline-flex">
            <FiEdit2 size={13} /> Edit
          </Button>
          <ActionDropdown items={menuItems} align="end" />
        </div>
      </div>
    </div>
  );
};

ApplicationDetailHeader.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }),
};

export default ApplicationDetailHeader;
